import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AllieList } from "../../../components/List";
import { AllieText } from "../../../components/Text";
import { AllieEllipsedText } from "../EllipsedText";
import { AllieUserAssigned } from "../UserAssigned";
import {
  AllieAccordionGroup,
  AllieAlert,
  AllieButton,
  AllieCard,
  AllieCheckbox,
  AllieIconButton,
  AllieInput,
  AllieItem,
  AllieLabel,
  AllieLink,
  AlliePopover,
  AllieTextArea,
} from "../../../components";
import {
  AllieAddIcon,
  AllieCheckmarkOutlineIcon,
  AllieCloseOutlineIcon,
  AllieCreateIcon,
  AllieEllipsisVerticalIcon,
  AllieThrashIcon,
} from "../../../styling/icons";
import { StyledAccordion, StyledItemDesktopBody } from "./Checklist.styles";
import { isTablet } from "../../../common/utils";
import { AllieChecklistItemProps, AllieChecklistProps } from "./Checklist";

export const AllieChecklistDesktop: React.FC<AllieChecklistProps> = ({
  title,
  titleMaxWidth = 315,
  description,
  addItemText = 'Add item',
  addItemDisabled = false,
  addItemTitlePlaceholder = 'Enter a title',
  emptyChecklistText = 'There is no sub-items created',
  descriptionPlaceholder = 'Description',
  items,
  operators,
  loggedUser,
  itemEditDisabled = false,
  itemRemoveDisabled = false,
  itemActionEditText = 'Edit',
  itemActionRemoveText = 'Remove',
  removeCancelText = 'Cancel',
  removeConfirmText = 'Ok',
  removeConfirmationTitle = 'You are about to remove an item.',
  onCreateItem,
  onEditItem,
  onRemoveItem,
  onDraftChange,
}) => {
  const [editorMode, setEditorMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AllieChecklistItemProps | null>();
  const [assignedToDraft, setAssignedToDraft] = useState<number | undefined>();  
  const [enableNewItemSave, setEnableNewItemSave] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<AllieChecklistItemProps | null>(null);
  const actionRefs = useRef<Record<number, HTMLIonPopoverElement>>({});
  const titleRef: RefObject<HTMLIonInputElement> = useRef(null);
  const descriptionRef: RefObject<HTMLIonTextareaElement> = useRef(null);

  useEffect(() => {
    if (!editorMode) {
      setEnableNewItemSave(false);
      setSelectedItem(null);
      setAssignedToDraft(undefined);
    }
  }, [editorMode]);

  const scrollToTop = () => {
    const el = document.getElementById('checklist-editor-section');
    el?.scrollIntoView({ behavior: 'smooth' });
  }

  const checkRequiredFields = () => !!titleRef.current?.value;

  useEffect(() => {
    assignedToDraft !== null
    && checkRequiredFields()
    && setEnableNewItemSave(true);
  }, [assignedToDraft]);

  useEffect(() => {
    if (selectedItem) {
      titleRef.current?.setAttribute('value', selectedItem.title || '');
      descriptionRef.current?.setAttribute('value', selectedItem.description || '');
    }
  }, [selectedItem]);

  const handleFormChange = () => {
    if (checkRequiredFields()) setEnableNewItemSave(true);
  };

  const draftMode = useMemo(() => Boolean(onDraftChange), [onDraftChange]);

  const handleChangeComplete = useCallback(
    (e: CustomEvent, item: AllieChecklistItemProps, index: number) => {
    if (e.detail.checked !== item.isDone) {
      if (draftMode) {
        items[index].isDone = e.detail.checked;
        onDraftChange?.(items);
      } else {
        onEditItem?.({ ...item, isDone: !item.isDone });
      }
    }
  }, [draftMode, onDraftChange, onEditItem]);

  const handleAddItem = useCallback((item: AllieChecklistItemProps, index: number) => {
    setEditorMode(true);
    setSelectedItem(item);
    actionRefs.current?.[index].dismiss();
  }, [actionRefs]);

  const handleChangeAssigned = useCallback(
    (e: CustomEvent, item: AllieChecklistItemProps, index: number) => (
    new Promise(() => {
      if (draftMode) {
        items[index].assignedToUid = e.detail.value;
        onDraftChange?.(items);
      } else {
        onEditItem && onEditItem({ ...item, assignedToUid: e.detail.value });
      }
    })
  ), [draftMode, onDraftChange, onEditItem]);

  const handleRemoveItem = useCallback((item: AllieChecklistItemProps, index: number) => {
    if (draftMode) {
      const safeItems = [...items]
      safeItems.splice(index, 1);
      onDraftChange?.(safeItems);
    } else {
      item?.id && onRemoveItem?.(item.id);
    }
    actionRefs.current?.[index].dismiss();
  }, [draftMode, onDraftChange, onRemoveItem, actionRefs]);

  const handleSaveItem = () => {
    if (selectedItem) {
      let targetIndex = 0;
      const safeItems = [ ...items ];
      safeItems.forEach((i, k) => { if (i.id === selectedItem.id) { targetIndex = k } });
      const safeItem = { ...safeItems[targetIndex] };
      safeItem.assignedToUid = assignedToDraft;
      safeItem.title = `${titleRef.current?.value}`;
      safeItem.description = `${descriptionRef.current?.value}`;
      safeItems.splice(targetIndex, 1, safeItem);
      if (draftMode) {
        onDraftChange?.(safeItems);
      } else {
        onEditItem && onEditItem(safeItem);
      }
    } else {
      const newItem = {
        title: `${titleRef.current?.value}`,
        description: `${descriptionRef.current?.value}`,
        assignedToUid: assignedToDraft,
        isRequired: true,
      }
      if (draftMode) {
        onDraftChange?.([ ...items, { ...newItem, id: new Date().getTime() * -1 } ]);
      } else {
        onCreateItem?.(newItem);
      }
    }
    setEditorMode(false);
  };

  const inputItem = () => (
    <>
      <AllieItem style={{ paddingTop: 15, marginTop: 0 }}>
        <div style={{ width: '60%', paddingRight: 15 }}>
          <AllieInput
            corner="rounded-edges"
            allieSize="small"
            type="text"
            placeholder={addItemTitlePlaceholder}
            ref={titleRef}
            onChange={handleFormChange}
          />
        </div>
        <AllieUserAssigned
          name={operators?.find((o) => o.value === selectedItem?.assignedToUid)?.text}
          operators={!selectedItem?.isDone ? operators : undefined}
          selected={operators?.find((o) => o.value === selectedItem?.assignedToUid)?.value}
          onSelect={(e: CustomEvent) => new Promise(() => setAssignedToDraft(e.detail.value))}
          selectProps={{ minWidth: '82%' }}
          width="40%"
        />
      </AllieItem>
      <AllieItem>
        <AllieTextArea
          ref={descriptionRef}
          placeholder={descriptionPlaceholder}
          onChange={handleFormChange}
          corner="rounded-edges"
        />
      </AllieItem>
      <AllieItem style={{ marginTop: 10 }}>
        <AllieIconButton
          disabled={!enableNewItemSave}
          type="secondary"
          onclick={handleSaveItem}
        >
          <AllieCheckmarkOutlineIcon color="success" />
        </AllieIconButton>
        <AllieIconButton type="secondary" onclick={() => setEditorMode(false)}>
          <AllieCloseOutlineIcon color="danger" />
        </AllieIconButton>
      </AllieItem>
    </>
  );

  const isLowResolution = useMemo(() => screen.width < 650, [screen]); 

  return (
    <div id="checklist-editor-section">
      {
        title && (
          <AllieItem>
            <AllieText slot="start" type="h4">{title}</AllieText>
            <AllieIconButton
              size="small"
              slot="end"
              allieType="secondary"
              corner="rounded-edges"
              disabled={addItemDisabled}
              onclick={() => {
                setEditorMode(true);
                scrollToTop();
              }}
            >
              <AllieAddIcon />
            </AllieIconButton>
          </AllieItem>
        )
      }
      <AllieItem>
        <AllieText type="body1">{description}</AllieText>
      </AllieItem>
      {
        (!editorMode || Boolean(items.length)) && (
          <AllieList
            style={{
              marginTop: 0,
              marginBottom: 0,
              paddingTop: 0,
              paddingLeft: 7.5,
            }}
          >
            {items.map((i, k) => {
              const assigned = operators?.find((o) => o.value === i.assignedToUid); 
              return (
                <AllieCard
                  content={{
                    style: { paddingLeft: 0, paddingRight: 0 },
                    children: (
                      <>
                      {
                        editorMode && (selectedItem?.id === i.id)
                        ? inputItem()
                        : (
                          <>
                            <div
                              key={k}
                              style={{
                                alignItems: 'center',
                                display: 'flex',
                                paddingRight: isLowResolution ? 70 : 80,
                              }}
                            >
                              {!draftMode && (
                                <div
                                  style={{
                                    marginLeft: isTablet ? 10: 20,
                                    position: 'absolute',
                                    left: 0,
                                  }}
                                >
                                  <AllieCheckbox
                                    style={{ verticalAlign: 'middle' }}
                                    checked={i.isDone}
                                    size={20}
                                    onIonChange={
                                      (e: CustomEvent) => handleChangeComplete(e, i, k)} />
                                </div>
                              )}
                              <StyledItemDesktopBody isTablet={isTablet} isDraft={draftMode}>
                                <AllieEllipsedText
                                  style={{
                                    textDecoration: `${i.isDone ? 'line-through' : ''}`,
                                    marginLeft: 0,
                                  }}
                                  triggerAction="hover"
                                  maxWidth={isLowResolution ? 195 : titleMaxWidth}
                                  width={isLowResolution ? 195 : titleMaxWidth}
                                  value={i.title || ''}
                                  triggerId={`checklist-item-${k}`}
                                  textProps={{ type: 'h4' }}
                                />
                                <AllieUserAssigned
                                  name={assigned?.value === loggedUser?.id
                                    ? loggedUser?.name : assigned?.text}
                                  operators={(!i.isDone && !itemEditDisabled)
                                    ? operators : undefined}
                                  selected={assigned?.value}
                                  onSelect={(e: CustomEvent) => handleChangeAssigned(e, i, k)}
                                  selectProps={{ margin: 0, width: 'inherit' }}
                                  nameMaxWidth={80}
                                  width={'120px'}
                                />
                              </StyledItemDesktopBody>
                              <div
                                style={{
                                  textAlign: 'right',
                                  paddingRight: isTablet ? 10 : 20,
                                  position: 'absolute',
                                  right: 0,
                                }}
                              >
                                <AllieText
                                  id={`edit-item-trigger-${k}`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <AllieEllipsisVerticalIcon />
                                </AllieText>
                              </div>
                            </div>
                            {
                              i.description && (
                                <AllieItem>
                                  {
                                    i.description.split(/\r\n|\r|\n/).length > 5 ? (
                                      <AllieAccordionGroup
                                        expand="inset"
                                        style={{ marginLeft: 0, marginRight: 0, width: '100%' }}
                                      >
                                        <StyledAccordion>
                                          <AllieItem slot="header" color="light">
                                            <AllieLabel>
                                              <AllieEllipsedText
                                                triggerId={`check-description-${i.id}`}
                                                value={i.description}
                                                maxWidth={'100%'}
                                                textProps={{ type: 'body2' }}
                                                tooltipEnabled={false}
                                              />
                                            </AllieLabel>
                                          </AllieItem>
                                          <div className="ion-padding" slot="content">
                                            <AllieText
                                              type="body1"
                                              style={{ whiteSpace: 'pre-wrap' }}
                                            >
                                              {i.description}
                                            </AllieText>
                                          </div>
                                        </StyledAccordion>
                                      </AllieAccordionGroup>
                                    ): (
                                      <AllieItem style={{ marginLeft: 10, marginTop: 0 }}>
                                        <AllieText
                                          type="body1"
                                          style={{ whiteSpace: 'pre-wrap' }}
                                        >
                                          {i.description}
                                        </AllieText>
                                      </AllieItem>
                                    )
                                  }
                                </AllieItem>
                              )
                            }
                            <AlliePopover
                              shadow={false}
                              trigger={`edit-item-trigger-${k}`}
                              triggerAction="click"
                              alignment="end"
                              ref={(ref: any) => {
                                if (ref) {
                                  actionRefs.current[k] = ref;
                                }
                              } }
                              content={{
                                children: (
                                  <AllieList style={{ backgroundColor: 'white', padding: 0 }}>
                                    <AllieItem
                                      disabled={itemEditDisabled || i.isDone}
                                      onclick={() => handleAddItem(i, k)}
                                    >
                                      <AllieLink allieSize="medium">
                                        <AllieCreateIcon />
                                        <span style={{ marginLeft: 10 }}>{itemActionEditText}</span>
                                      </AllieLink>
                                    </AllieItem>
                                    <AllieItem
                                      disabled={itemRemoveDisabled || i.isDone}
                                      onclick={() => handleRemoveItem(i, k)}
                                    >
                                      <AllieLink allieSize="medium">
                                        <AllieThrashIcon />
                                        <span style={{ marginLeft: 10 }}>
                                          {itemActionRemoveText}
                                        </span>
                                      </AllieLink>
                                    </AllieItem>
                                  </AllieList>
                                )
                              }}
                            />
                          </>
                        )
                      }
                      </>
                    )
                  }}
                />
              )}
            )}
            {!items.length && !editorMode && (
              <AllieItem>
                <div style={{ textAlign: 'center', width: '100%' }}>
                  <AllieText type="h4" color="light-40" weight={600}>
                    {emptyChecklistText}
                  </AllieText>
                </div>
              </AllieItem>
            )}
            {
              !editorMode && (
                <div style={{ textAlign: 'center', marginTop: 15 }}>
                  <AllieButton
                    allieSize="small"
                    corner="rounded-edges"
                    allieType="secondary"
                    onclick={() => {
                      setEditorMode(true);
                      scrollToTop();
                    }}
                  >
                    {addItemText}
                  </AllieButton>
                </div>
              )
            }
          </AllieList>
        )
      }
      {
        editorMode && !selectedItem && inputItem()
      }
      <AllieAlert
        header={removeConfirmationTitle}
        isOpen={Boolean(itemToRemove)}
        buttons={[
          {
            text: removeCancelText,
            handler: () => {
              setItemToRemove(null);
            },
          },
          {
            text: removeConfirmText,
            handler: () => {
              itemToRemove?.id && onRemoveItem?.(itemToRemove.id);
              setItemToRemove(null);
            },
          },
        ]}
      />
    </div>
  );
};
